import { defineStore } from 'pinia';
import { AxiosError } from 'axios';
import { ref } from 'vue';
import {
  PersonBuildingInterface,
  OrganizationBuildingInterface,
  PersonBuildingRequest,
  OrganizationBuildingRequest,
} from '@/types/BuildingInterface';
import BuildingService from '@/services/BuildingService';
import { handleApiCall } from '@/utils/storeHelpers';

export const useBuildingStore = defineStore('BuildingStore', () => {
  const personBuildings = ref<PersonBuildingInterface[]>([]);
  const organizationBuildings = ref<OrganizationBuildingInterface[]>([]);
  const loading = ref(false);
  const error = ref<AxiosError | unknown | null>(null);
  const totalRecords = ref(0);
  const totalBuildingsRecords = ref(0);

  async function fetchPersonBuildings(person_id: number) {
    return handleApiCall(
      () => BuildingService.getPersonBuildings(person_id),
      loading,
      error,
      (res) => {
        personBuildings.value = res;
        totalRecords.value = res.length;
      },
    );
  }

  async function fetchOrganizationBuildings(organization_id: number) {
    return handleApiCall(
      () => BuildingService.getOrganizationBuildings(organization_id),
      loading,
      error,
      (res) => {
        organizationBuildings.value = res;
        totalBuildingsRecords.value = res.length;
      },
    );
  }

  async function attachPersonBuilding(data: PersonBuildingRequest, person_id: number) {
    return handleApiCall(() => BuildingService.attachPersonBuilding(data, person_id), loading, error);
  }

  async function detachPersonBuilding(data: PersonBuildingRequest, person_id: number) {
    return handleApiCall(() => BuildingService.detachPersonBuilding(data, person_id), loading, error);
  }

  async function deleteOrganizationBuilding(building_id: number, organization_id: number) {
    return handleApiCall(
      () =>
        BuildingService.deleteOrganizationBuilding(building_id).then(() => fetchOrganizationBuildings(organization_id)),
      loading,
      error,
    );
  }

  async function createOrganizationBuilding(data: OrganizationBuildingRequest) {
    return handleApiCall(() => BuildingService.createOrganizationBuilding(data), loading, error);
  }

  async function updateOrganizationBuilding(data: OrganizationBuildingRequest, building_id: number) {
    return handleApiCall(() => BuildingService.updateOrganizationBuilding(data, building_id), loading, error);
  }

  return {
    personBuildings,
    organizationBuildings,
    loading,
    error,
    totalRecords,
    totalBuildingsRecords,
    fetchPersonBuildings,
    fetchOrganizationBuildings,
    attachPersonBuilding,
    detachPersonBuilding,
    deleteOrganizationBuilding,
    createOrganizationBuilding,
    updateOrganizationBuilding,
  };
});

export default useBuildingStore;
